/* eslint-disable no-undef */
import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { convertGoogleLatLng } from '@core/utils/location/addressHandler.utils';
import { fitMapBounds } from '@core/utils/map/mapBounds';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { selectRoute, selectCenter, selectZoom } from '../../core/store/modules/map/mapSelectors';
import { setCenter, setZoom } from '../../core/store/modules/map/mapSlice';
import Circles from './components/Circles';
import InfoWindow from './components/InfoWindow';
import Markers from './components/Markers';
import PinCluster from './components/PinCluster';
import Polygons from './components/Polygons';
import Route from './components/Route';
import { mapConfig } from 'Utils/map/googleMaps.config';
import { addMapLogs } from '@core/api/account/account.service';
import { useLocation } from 'react-router-dom';
import Coverages from '~/Map/components/Coverages';

const containerStyle = { height: '100%', borderRadius: '7px' };
const Map = forwardRef((props, ref) => {
  const { id = null, zoom, center, autoCenter, fitBounds, options, initMap, enableAnimation, coverage, ...mapProps } = props;
  // Props to be drawn in the map
  const { markers, marker, agentMarkers, polygons, circles, onCircleClick, onPolygonClick, infoWindow } = mapProps;
  const mapRef = useRef();
  const dispatch = useDispatch();
  const route = useSelector(selectRoute);
  const mapZoom = useSelector(selectZoom);
  const mapCenter = useSelector(selectCenter);
  const location = useLocation();
  const [googleMap, setGoogleMap] = useState(null);
  const [mapOptions, setMapOptions] = useState({
    zoom: mapZoom,
    center: mapCenter,
    streetViewControl: false,
    mapTypeControlOptions: { mapTypeIds: [] },
    mapId: mapConfig.mapCoverageId,
    ...options,
  });
  const { isLoaded, loadError } = useJsApiLoader(mapConfig);
  useImperativeHandle(ref, () => ({ fitMap, fitBoundsMap, getCurrentZoom, getCurrentCenter }));
  // Center/zoom bootstrap
  useEffect(() => {
    if (center) {
      dispatch(setCenter(center));
    } else if (autoCenter) {
      if (navigator.geolocation && Object.keys(navigator.geolocation).length !== 0) {
        navigator.geolocation.getCurrentPosition((pos) => {
          const centerCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          dispatch(setCenter(centerCoords));
        });
      } else {
        dispatch(setZoom(2));
      }
    }
  }, [autoCenter, center, dispatch]);
  useEffect(() => {
    if (zoom && zoom !== mapZoom) dispatch(setZoom(zoom));
  }, [zoom, mapZoom, dispatch]);
  useEffect(() => {
    fitMap();
  }, [googleMap, markers, agentMarkers, marker, polygons, circles]);
  useEffect(() => {
    setMapOptions((prev) => ({
      ...prev,
      zoom: mapZoom,
      center: mapCenter,
      streetViewControl: false,
      mapTypeControlOptions: { mapTypeIds: [] },
      mapId: mapConfig.mapCoverageId,
      ...options,
    }));
  }, [mapCenter, mapZoom, options]);
  // --- Map Markers/Routes ---
  /*const MapMarkers = () => {
    let normalMarkers = null;
    let agMarkers = null;
    if (marker || markers) normalMarkers = <PinCluster marker={marker} markers={markers} enableAnimation={enableAnimation} />;
    if (agentMarkers) agMarkers = <Markers markers={agentMarkers} enableAnimation={enableAnimation} />;
    if (fitBounds && googleMap != null) fitMapBounds(googleMap, markers, agentMarkers, marker);
    if (normalMarkers || agMarkers) return (<>{normalMarkers}{agMarkers}</>);
    return null;
  };*/
  const normalMarkerLayer = React.useMemo(() => {
  if (!marker && (!markers || markers.length === 0)) return null;
  return (
    <PinCluster key="normal-markers" marker={marker} markers={markers} enableAnimation={enableAnimation} />);
  }, [marker, markers, enableAnimation]);
  const agentMarkerLayer = React.useMemo(() => {
    if (!agentMarkers) return null;

    const hasMarkers = Array.isArray(agentMarkers)
      ? agentMarkers.some((markerGroup) => Array.isArray(markerGroup?.markers) && markerGroup.markers.length > 0)
      : Array.isArray(agentMarkers.markers) && agentMarkers.markers.length > 0;

    if (!hasMarkers) return null;

    return (
      <Markers key="agent-markers" markers={agentMarkers} enableAnimation={enableAnimation} />);
  }, [agentMarkers, enableAnimation]);
  /*const agentMarkerLayer = React.useMemo(() => {
  if (!agentMarkers || !agentMarkers.markers || agentMarkers.markers.length === 0) return null;
  return (
    <Markers key="agent-markers" markers={agentMarkers} enableAnimation={enableAnimation} />);
  }, [agentMarkers, enableAnimation]);*/
  // only refit bounds when inputs actually change
  useEffect(() => {
    if (!googleMap || !fitBounds) return;
    fitMapBounds(googleMap, markers, agentMarkers, marker, polygons, circles);
  }, [googleMap, fitBounds, markers, agentMarkers, marker, polygons, circles]);
  const Routes = () => (route ? <Route /> : null);
  // --- Map load ---
  const onLoad = (map) => {
    if (map != null) {
      setGoogleMap(map);
      if (initMap) initMap(map);
    }
    const body = { pageName: location.pathname.substring(1) };
    addMapLogs(body);
  };
  const getCurrentZoom = () => (mapRef.current ? mapRef.current.state.map.zoom : null);
  const getCurrentCenter = () => {
    if (!mapRef.current) return null;
    const { center: mapRefCenter } = mapRef.current.state.map;
    return convertGoogleLatLng(mapRefCenter);
  };
  const fitMap = () => fitMapBounds(googleMap, markers, agentMarkers, marker, polygons, circles);
  const fitBoundsMap = (bounds) => {
    if (!googleMap) return;
    googleMap.fitBounds(bounds);
    dispatch(setCenter(getCurrentCenter()));
    dispatch(setZoom(getCurrentZoom()));
  };
  if (loadError) return <p> Error Loading the Map </p>;
  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      options={mapOptions}
      onLoad={onLoad}
      ref={mapRef}>
      {normalMarkerLayer}
      {agentMarkerLayer}
      <Routes />
      <Polygons polygonData={polygons} onPolygonClick={onPolygonClick} hasCoverageBoundary={coverage?.enabled} />
      <Coverages coverage={coverage} googleMap={googleMap} />
      <Circles circleData={circles} onCircleClick={onCircleClick} />
      <InfoWindow infoWindowData={infoWindow} />
    </GoogleMap>
  ) : (
    <></>
  );
});
Map.propTypes = {
  id: PropTypes.string,
  zoom: PropTypes.number,
  center: PropTypes.object,
  autoCenter: PropTypes.bool,
};
export default React.memo(Map);
